import { Component, OnInit, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { NgbTabChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { Router, ActivatedRoute } from '@angular/router';
import { ContactPickerComponent } from '../../child-component/contact-picker/contact-picker.component';

import Swal from 'sweetalert2';

@Component({
  selector: 'app-client-info',
  templateUrl: './client-info.component.html',
  styleUrls: ['./client-info.component.scss']
})
export class ClientInfoComponent implements OnInit {
	page_title = "Inspection: Responsible Parties"
	@ViewChild('intakeTab') ngbTabSet;
	// Only the active tab's picker is rendered, so this holds at most one.
	@ViewChildren(ContactPickerComponent) pickers: QueryList<ContactPickerComponent>;

  	constructor(
        private  _router : Router,
        private  router : ActivatedRoute
    ) { }

  	new_tab:any;
  	selectedTab:any;
    jobId:any;
    // Set while we re-issue a tab change the user confirmed discarding.
    private discarding = false;

  	ngOnInit() {
  	    this.router.params.subscribe(params => {
            if(!params.jobId){
                localStorage.setItem('new_tab', JSON.stringify('buyer_agent_id') );
            } else {
                this.jobId = params.jobId;
            }
        })
  	}

  	ngAfterViewInit() {
  		setTimeout(()=>{
      		if(localStorage.getItem("new_tab") != null){
				this.new_tab = (JSON.parse(localStorage.getItem("new_tab")));
				this.selectedTab = this.new_tab
                localStorage.removeItem('new_tab');
			} else {
				this.selectedTab = 'buyer_agent_id';
			}
 		}, 500);
  	}

    /**
     * Switching tabs destroys the picker, so anything unsaved would go with it.
     *
     * Exactly one dialog is allowed per switch. clientSubmit() can raise a
     * chain of prompts (missing address, shared-contact edit); stacking those
     * on top of a save confirmation would make moving between parties worse
     * than the Save and continue it replaces. So the picker reports up front
     * whether it can be saved, and only the shared-contact question - which
     * affects other jobs and cannot be answered for the user - is still asked.
     */
    public beforeChange($event: NgbTabChangeEvent) {
        if (this.discarding) {
            this.rememberTab($event.nextId);
            return;
        }

        const picker = this.activePicker();
        if (!picker || !picker.isDirty()) {
            this.rememberTab($event.nextId);
            return;
        }

        $event.preventDefault();

        const blocked = picker.blockingReason();
        if (blocked) {
            this.confirmDiscard($event.nextId, blocked);
            return;
        }

        if (picker.needsContactUpdateChoice()) {
            this.askContactUpdate(picker, $event.nextId);
            return;
        }

        // A new job does not exist until the first party is saved, so saving
        // silently here would create one just because someone clicked around.
        if (!this.jobId) {
            Swal({
                title: 'Save this party?',
                text: 'Save these details before switching to another party?',
                type: 'question',
                showCancelButton: true,
                confirmButtonText: 'Save and switch',
                cancelButtonText: 'Stay on this tab',
            }).then(result => {
                if (result.value) {
                    this.saveThenSwitch(picker, false, $event.nextId);
                }
            });
            return;
        }

        // Existing job: the save is an update, so it needs no confirmation.
        this.saveThenSwitch(picker, false, $event.nextId);
    }

    private confirmDiscard(nextId: string, reason: string) {
        Swal({
            title: 'This party cannot be saved yet',
            text: reason + ' Leaving this tab will discard these changes.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Discard changes',
            cancelButtonText: 'Stay on this tab',
        }).then(result => {
            if (result.value) {
                this.switchTo(nextId);
            }
        });
    }

    // Editing a contact touches every job that shares it, so this one stays a
    // question rather than being answered on the user's behalf.
    private askContactUpdate(picker: ContactPickerComponent, nextId: string) {
        Swal({
            title: 'This contact has been edited',
            text: 'These details belong to a contact that other jobs may also use. Update the contact everywhere, or link it as it is stored?',
            type: 'question',
            showCancelButton: true,
            confirmButtonText: 'Update contact everywhere',
            cancelButtonText: 'Link as stored',
        }).then(result => {
            // Esc or a click outside is not an answer to this question, so it
            // leaves the user where they are rather than picking for them.
            const dismiss: any = result.dismiss;
            if (dismiss && dismiss !== 'cancel') {
                return;
            }

            const updateEverywhere = !!result.value;
            if (!updateEverywhere) {
                picker.restoreOriginalContact();
            }
            this.saveThenSwitch(picker, updateEverywhere, nextId);
        });
    }

    private saveThenSwitch(picker: ContactPickerComponent, updateContact: boolean, nextId: string) {
        picker.saveInPlace(updateContact).then(
            data => {
                // The first save on a new job is what brings the job into
                // being. Put its id in the URL so every later tab resolves it
                // the ordinary way and a refresh no longer loses the job.
                if (!this.jobId && data && data.job_id) {
                    this.jobId = data.job_id;
                    // Same route, so Angular reuses this component: params fire
                    // again but ngAfterViewInit does not, which means the tab
                    // has to be switched here rather than handed over through
                    // the new_tab entry the first-load path uses.
                    this._router.navigate(['/inspections-order-parties', data.job_id])
                        .then(() => this.switchTo(nextId));
                    return;
                }
                this.switchTo(nextId);
            },
            () => {
                Swal({
                    title: 'Could not save',
                    text: 'This party was not saved, so you are still on the same tab. Check your connection and try again.',
                    type: 'error'
                });
            }
        );
    }

    private switchTo(nextId: string) {
        this.discarding = true;
        this.ngbTabSet.select(nextId);
        this.discarding = false;
    }

    private rememberTab(nextId: string) {
        if (!this.jobId) {
            localStorage.setItem('new_tab', JSON.stringify(nextId));
            this.selectedTab = nextId;
        }
    }

    private activePicker(): ContactPickerComponent {
        return this.pickers && this.pickers.length ? this.pickers.first : null;
    }
}
